<template>
  <ChatDialogShell v-model="isOpen" :inline="inline" size="xxlarge" icon="heroicons:clipboard-document-list"
    @close="emit('resetModal')">
    <Stack gap="large">
      <!-- Subject Section -->
      <Stack>
        <transition
          enter-active-class="transition ease-in duration-300"
          enter-from-class="transform opacity-0"
          enter-to-class="transform opacity-100 scale-100"
          leave-active-class="transition ease-in duration-300"
          leave-from-class="transform opacity-100 scale-100"
          leave-to-class="transform opacity-0"
        >
          <Label v-show="taskForm.subject">Task Name</Label>
        </transition>

        <Box class="relative" gapX="small">
          <span v-if="!taskForm.subject" class="absolute z-10 left-[120px] top-1 self-center text-base text-red-900 dark:text-red-500">*</span>
          <InputText
            id="taskName"
            v-model="taskForm.subject"
            placeholder="Task Name"
            minimal
            size="small"
            icon="heroicons:user"
            class="w-full"
          />
        </Box>
      </Stack>

      <!-- Description  -->
      <Stack>
        <transition
          enter-active-class="transition ease-in duration-300"
          enter-from-class="transform opacity-0"
          enter-to-class="transform opacity-100 scale-100"
          leave-active-class="transition ease-in duration-300"
          leave-from-class="transform opacity-100 scale-100"
          leave-to-class="transform opacity-0"
        >
          <Label v-show="taskForm.comment">Description</Label>
        </transition>
        <Textarea
          id="commentTextarea"
          v-model="taskForm.comment"
          rows="2"
          placeholder="Description"
          minimal
        />
      </Stack>

      <!-- Assignee -->
      <Stack>
        <Label :required="!taskForm.assignee">
          Assignee
          <!-- If assigned by someone else -->
          <p v-if="taskToUpdate?.assignee && taskToUpdate.assignee.id != taskToUpdate.author.id" class="ml-1.5 italic text-charcoal-600 dark:text-charcoal-500">( assigned by: {{ taskToUpdate.author.name }} )</p>
        </Label>
        <AutoComplete
          v-model="taskForm.assignee"
          :options="assignToArray"
          optionLabel="name"
          :apiSearch="searchAssigneeByKeyword"
          completeOnFocus
          showClear
          :delay="500"
          minimal
        />
      </Stack>

      <!-- Related To -->
      <Stack>
        <Box gapX="xsmall" alignItems="center">
          <Label>Related to</Label>
          <Box gapX="xxsmall">
            <Text v-if="!taskForm.relatedTo.id" class="opacity-70" italic size="xsmall">(Recommended)</Text>
            <Icon icon="heroicons:information-circle" class="text-charcoal-500 dark:text-charcoal-500" v-tippy="{ content: 'It is a best practice to ensure you align a task with a specific Account, Opportunity, or Product Adoption entry' }" />
          </Box>
          <!-- To display selected Accounts since it's a multiselect now -->
          <Box v-if="selectedAccounts.length" gapX="small" gapY="xsmall" full marginTop="xxsmall">
            <Tag
              v-for="account in selectedAccounts"
              :key="account.value"
              :value="account.label"
              size="xsmall"
              nullable
              tone="info"
              @clear="clearSelectedAccount"
            />
          </Box>
        </Box>
        <Box class="relative" full>
          <span @click="disableRelatedTo ? null : relatedToOpen = !relatedToOpen" type="button" class="relative inline-flex items-center px-1 py-1 text-sm font-medium border-0 border-b border-charcoal-300 dark:border-charcoal-600 group-focus-within:border-iris-400 dark:group-focus-within:border-iris-400 group-focus-within:border-b-2" :disabled="disableRelatedTo">
            <Icon :icon="taskForm.relatedTo.icon"  class="w-7 h-7 rounded-sm p-1" :class="taskForm.relatedTo.color"/>
            <Icon v-if="!disableRelatedTo" :icon="relatedToOpen ? 'heroicons:chevron-up' : 'heroicons:chevron-down'" class="ml-1 w-3 h-3" />
          </span>
          <span v-if="relatedToOpen" @click="relatedToOpen = false" class="fixed top-0 right-0 bottom-0 left-0 w-full h-full z-10"></span>
          <div v-if="relatedToOpen" class="absolute top-11 z-10">
            <ul class="cursor-pointer overflow-auto max-h-48 rounded-md bg-white py-1 text-sm border border-charcoal-300 focus:outline-hidden dark:bg-charcoal-950 dark:border-charcoal-700">
              <li @click="selectRelatedToObjectType( task )" v-for="(task, index) in relatedToObjectTypeArray" :key="index">
                <div class="flex items-center w-full text-left py-2 pl-3 pr-7 text-charcoal-900 hover:bg-iris-600 hover:text-white dark:text-charcoal-200">
                  <Icon :icon="task.icon"  class="w-7 h-7 mr-3 rounded-sm p-1" :class="task.color"/>
                  <p>{{ task.type }}</p>
                </div>
              </li>
            </ul>
          </div>

          <AutoComplete
            v-if="taskForm.relatedTo.type !== 'Account'"
            v-model="taskForm.relatedTo.id"
            :options="filteredResults"
            completeOnFocus
            :placeholder="`Search for ${taskForm.relatedTo.type}...`"
            :disabled="disableRelatedTo"
            :apiSearch="searchForRelatedTo"
            showClear
            minimal
            class="flex-1"
          />

          <Box v-if="taskForm.relatedTo.type === 'Account'" class="relative flex-1">
            <Text
              v-if="existingAccountSelected"
              paddingLeft="small"
              marginTop="xxsmall"
              color="light"
              class="flex-1 border-b border-charcoal-300 dark:border-charcoal-600 cursor-not-allowed"
            >
              {{ taskForm.relatedTo.name }}
            </Text>
            <IconField v-else class="flex flex-1">
              <InputText
                v-model="taskForm.relatedTo.accountSearchTerm"
                minimal
                placeholder="Search for Account(s)"
                class="flex-1"
                @focus="relatedToForAccountsOpen = true"
                @input="searchForRelatedAccounts"
                nullable
              />
              <Loader v-if="accountsLoading" :size="18" color="#bbb" class="self-center absolute right-10" />
            </IconField>
            <!-- Overlay to close dropdown -->
            <span v-if="relatedToForAccountsOpen" @click="closeAccountSearchDropdown" class="fixed top-0 right-0 bottom-0 left-0 w-full h-full z-10"></span>
            <!-- Dropdown -->
            <div v-if="relatedToForAccountsOpen" class="absolute top-11 z-10 w-full">
              <ul class="cursor-pointer overflow-auto max-h-60 rounded-md bg-white py-1 text-sm border border-charcoal-300 focus:outline-hidden dark:bg-charcoal-900 dark:border-charcoal-700">
                <li v-if="!filteredAccounts.length" class="py-2 pl-3 text-charcoal-900 dark:text-charcoal-200">No results found</li>

                <li v-for="result in filteredAccounts" :key="result.value">
                  <div @click="selectRelatedToAccount(result)" class="flex items-center w-full py-2 pl-3 pr-7 text-charcoal-900 hover:bg-iris-600 hover:text-white dark:text-charcoal-200">
                    <p class="flex-1">{{ result.label }}</p>
                    <Icon
                      v-if="result.trailingIcon"
                      :icon="result.trailingIcon.icon"
                      class="size-5 text-green-700 dark:text-green-600"
                      v-tippy
                      :content="result.trailingIcon.tippy"
                    />
                  </div>
                </li>
              </ul>
            </div>
          </Box>
        </Box>
      </Stack>

      <!-- Products -->
      <Stack v-if="!pnpRecordType">
        <Label>Products</Label>
        <div class="grid grid-cols-2 gap-x-6 gap-y-4 w-full sm:flex-row sm:grid-cols-4 mt-1">
          <div
            v-for="product in products"
            :key="product.name"
            @click="selectProduct(product.name)"
            class="py-0 5 rounded-full cursor-pointer"
            :class="taskForm.products.includes(product.name) ? 'bg-iris-400/20' : 'bg-charcoal-400/20'"
          >
            <Box justifyContent="between" alignItems="center" paddingX="small" paddingY="xxsmall">
              <div :class="taskForm.products.includes(product.name) ? 'dark:text-iris-200 text-primary-link' : 'text-charcoal-600 dark:text-charcoal-400'" class="text-sm">{{ product.name}}</div>
              <Icon v-if="taskForm.products.includes(product.name)" icon="heroicons:check" class="w-4 h-4 text-iris-200 bg-primary-link rounded-full" aria-hidden="true"/>
              <Icon v-else icon="heroicons:plus" class="w-4 h-4 text-charcoal-600 dark:text-charcoal-400" aria-hidden="true"/>
            </Box>
          </div>
        </div>
      </Stack>

      <div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <!-- Task Type -->
        <Stack>
          <Label>Type</Label>
          <Select
            v-model="taskForm.type"
            :options="typePicklist"
            minimal
            :disabled="pnpRecordType"
            class="w-full"
          >
            <template #input="{ value }">
              <Box gapX="small" alignItems="center">
                 <Icon
                 icon="heroicons:clipboard"
                 class="size-5 text-green-600"
                 :class="[pnpRecordType ? 'cursor-not-allowed opacity-70' : null]"
                />
                <Text v-if="!value" color="light">
                  Select Task Type
                </Text>
                <Text v-else>
                  {{ value }}
                </Text>
              </Box>
            </template>
          </Select>
        </Stack>

        <!-- Task Status -->
        <Stack>
          <Label>Status</Label>
          <Select
            v-model="taskForm.status"
            :options="statusPicklist"
            minimal
            class="w-full"
          >
            <template #input="{ value }">
              <Box gapX="small" alignItems="center">
                 <Icon
                 icon="heroicons:adjustments-vertical"
                 class="size-5 text-blue-700 dark:text-blue-500"
                />
                <Text>
                  {{ value }}
                </Text>
              </Box>
            </template>
          </Select>
        </Stack>

        <!-- Task Communication Status -->
        <Stack v-if="pnpRecordType">
          <Label>Communication Status</Label>
          <Select
            v-model="taskForm.communicationStatus"
            :options="communicationStatusPicklist"
            minimal
            class="w-full"
          >
            <template #input="{ value }">
              <Box gapX="small" alignItems="center" noWrap>
                 <Icon
                 icon="heroicons:clipboard-document-check"
                 class="size-5 text-orange-500"
                />
                <Text v-if="!value" color="light">
                  Select Communication Status
                </Text>
                <Text v-else>
                  {{ value }}
                </Text>
              </Box>
            </template>
          </Select>
        </Stack>

        <!-- Task Conversion Status -->
        <Stack v-if="pnpRecordType">
          <Label>Conversion Status</Label>
          <Select
            v-model="taskForm.conversionStatus"
            :options="conversionStatusPicklist"
            minimal
            class="w-full"
          >
            <template #input="{ value }">
              <Box gapX="small" alignItems="center" noWrap>
                 <Icon
                 icon="heroicons:arrows-right-left-16-solid"
                 class="size-5 text-yellow-500 dark:text-yellow-600"
                />
                <Text v-if="!value" color="light">
                  Select Conversion Status
                </Text>
                <Text v-else>
                  {{ value }}
                </Text>
              </Box>
            </template>
          </Select>
        </Stack>

        <!-- Task Estimated Start Date -->
        <Stack v-if="pnpRecordType">
          <Label>Estimated Start Date</Label>
          <DatePicker
            v-model="taskForm.estimatedStartDate"
            placeholder="Estimated Start Date"
            minimal
          />
        </Stack>

        <!-- Task Due Date -->
        <Stack>
          <Label>Due Date</Label>
          <DatePicker
            v-model="taskForm.date"
            minimal
          />
        </Stack>

        <!-- Task Duration -->
        <Stack v-if="!pnpRecordType">
          <Label>Duration <span class="ml-1.5 text-charcoal-600 dark:text-charcoal-500">(hrs)</span></Label>
          <Select
            v-model="taskForm.duration"
            :options="durationArray"
            minimal
            class="w-full"
          >
            <template #input="{ value }">
              <Box gapX="small" alignItems="center" noWrap>
                 <Icon
                 icon="heroicons:clock"
                 class="size-5 text-[#A094ED]"
                />
                <Text v-if="!value" color="light">
                  Select Task Duration
                </Text>
                <Box v-else gapX="xsmall" alignItems="center">
                  <Text>
                    {{ value }}
                  </Text>
                  <Text size="small" color="light">{{ value === 1 ? 'hr.' : 'hrs.' }}</Text>
                </Box>
              </Box>
            </template>

            <template #options="{ option }">
              <Box gapX="xsmall" alignItems="center">
                <Text>{{ option.label }}</Text>
                <Text size="small" color="light">{{ option.label === 1 ? 'hr.' : 'hrs.' }}</Text>
              </Box>
            </template>
          </Select>
        </Stack>

        <!-- Task Contact -->
        <Stack>
          <Label>Contact</Label>
          <Box alignItems="center">
            <Box class="border-b py-2.5 border-charcoal-300 dark:border-charcoal-600">
              <Icon icon="heroicons:user" class="size-5 text-lime-600 dark:text-lime-500 ml-3"/>
            </Box>
            <AutoComplete
              v-model="taskForm.contact.label"
              :options="contactSearchResults"
              completeOnFocus
              :apiSearch="searchContacts"
              placeholder="Search Contacts"
              showClear
              icon="heroicons:user"
              iconColor="text-lime-600 dark:text-lime-500"
              minimal
              class="flex-1"
            >
             <template #options="{ option }">
              <Stack>
                <Text>{{ option.label }}</Text>
                <Text size="small" color="light">{{ option.email  }}</Text>
              </Stack>
             </template>
            </AutoComplete>
          </Box>
        </Stack>

        <!-- Task Priority -->
        <Stack>
          <Label>Priority</Label>
          <Select
            v-model="taskForm.priority"
            :options="priorityPicklist"
            minimal
            class="w-full"
          >
            <template #input="{ value }">
              <Box gapX="small" alignItems="center" noWrap>
                <Icon
                 icon="heroicons:flag"
                 class="size-5"
                 :class="priorityIconColor"
                />
                <Text>
                  {{ value }}
                </Text>
              </Box>
            </template>
          </Select>
        </Stack>
      </div>

      <!-- Task Reminder Controls-->
      <Box gapY="small">
        <Box
          gapX="xsmall"
          alignItems="center"
          v-tippy="{ onShow: showReminderDisabledTippy }" :content="tippyContent"
        >
            <Label :class="dueDateHasPassed || taskForm.status == 'Completed' ? 'opacity-50' : null">Remind me about this Task</Label>
            <Toggle v-model="remindMe" size='small' :disabled="dueDateHasPassed || taskForm.status == 'Completed'" />
        </Box>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-6 w-full">
          <Select
            :key="reminderKey"
            v-if="remindMe"
            v-model="taskForm.reminderInterval"
            :options="availableTimes"
            minimal
          >
            <template #input="{ value }">
              <Box gapX="small" alignItems="center" noWrap>
                <Icon
                 icon="heroicons:bell"
                 class="size-5 text-charcoal-800 dark:text-charcoal-200"
                />
                <Text v-if="!value" color="light">
                  Select Reminder Interval
                </Text>
                <Text v-else>
                  {{ value }}
                </Text>
              </Box>
            </template>
          </Select>


          <DatePicker
            v-if="remindMe && taskForm.reminderInterval === 'Custom'"
            v-model="taskForm.customReminderDate"
            :showTime=true
            :stepMinute=30
            :minDate="new Date()"
            :maxDate="determineReminderConstrict(taskForm.date)"
            minimal
          />
        </div>
      </Box>
    </Stack>
    <!-- End of Modal Stack -->

    <Stack marginTop="xlarge" gap="small">
      <Box v-if="apiError.show" justifyContent="center" gapX="xxsmall" class="text-red-600 text-center">
        <span>There was an error {{editing ? 'editing' : 'submitting'}} the Task.</span>
        <Icon icon="heroicons:exclamation-circle" class="h-5 w-5 mr-1 text-red-600 hover:text-red-700 self-center cursor-pointer" fill="currentColor" v-tippy :content="apiError.tippy"/>
      </Box>
      <Box justifyContent="end">
        <Box gapX="gutter">
          <Button @click="emit('close-modal')">Cancel</Button>
          <Button @click="submitTask" color="purple" :disabled="formSubmitDisabled">
            <span v-if="!editing">Add Task</span>
            <span v-else>Save</span>
            <Loader v-if='submitForm && !deleteForm' :size="18" color="#fff" class="ml-2 self-center" />
          </Button>
        </Box>

        <Box v-if="editing" @click="emit('deleteTask', taskToUpdate), deleteForm = true" class="cursor-pointer">
          <SalesforceIcon :objectId="taskToUpdate.id" class="w-7 h-7 ml-6 mr-3 self-center text-primary-link"/>
          <Loader v-if="!submitForm && deleteForm" :size="24" color="#C82424" class="mr-2 self-center" />
          <Icon v-else icon="heroicons:trash" class="h-7 w-7 mr-1 text-red-700 hover:text-red-800 self-center" fill="currentColor"/>
        </Box>
      </Box>
    </Stack>
  </ChatDialogShell>
</template>

<script setup>
import { ref, computed, onMounted, inject, watch } from 'vue'
import ChatDialogShell from '@/components/Agent/ChatDialogShell.vue'
import { Icon } from '@iconify/vue'
import Loader from '@/components/Core/Loader.vue'
import { uniqBy, isEmpty, debounce } from 'lodash'
import { v2 } from '@/services/api.js'
import Button from '@/components/Core/Button.vue'
import { prettyDateToDateString } from '@/js/utils.js'
import SalesforceIcon from '@/components/Core/SalesforceIcon.vue'
import Text from '@/components/Core/Text.vue'
import Label from '@/components/Core/Form/Label.vue'
import IconField from 'primevue/iconfield'
import InputText from '@/components/Core/Form/InputText.vue'
import Textarea from '@/components/Core/Form/Textarea.vue'
import { useTasksPicklist } from '@/composables/Task/useTasksPicklist.js'
import { Box, Stack } from '@/components/Core/Layout'
import Toggle from '@/components/Core/Toggle.vue'
import DatePicker from '@/components/Core/DatePicker.vue'
import { setHours, setMinutes, roundToNearestMinutes } from 'date-fns'
import Tag from '@/components/Core/Tag.vue'
import AutoComplete from '@/components/Core/Form/AutoComplete.vue'
import Select from '@/components/Core/Form/Select.vue'


const props = defineProps({
  assignedUser: {
    type: Object
  },
  fromTopLevelObject: {
    type: Object,
  },
  // Renders as an inline chat card instead of a popup Dialog — see ChatDialogShell.vue.
  inline: {
    type: Boolean,
    default: false
  },
  limit: {
    type: Number,
    default: 50
  },
  openModal: {
    type: Boolean
  },
  team: {
    type: Object
  },
  taskToUpdate: {
    type: Object
  },
})

const isOpen = defineModel('openModal')

const emit = defineEmits(['close-modal', 'resetModal', 'deleteTask', 'submitTask'])

const remindMe = ref(false)

// Check if the duedate of the task has passed to determine if reminders are allowed to be set
const dueDateHasPassed = computed(() => {
  const now = new Date()
  const dueDate = new Date(taskForm.value.date)

  // Reminders are allowed if the due date is today, as long as the current time hasn't passed midnight.
  return dueDate < now.setHours(0, 0, 0, 0)
})

function showReminderDisabledTippy() {
  return dueDateHasPassed.value != false || taskForm.value.status == 'Completed'
}

const tippyContent = computed(() => {
  if (dueDateHasPassed.value) {
    return '<span class=\'text-xs italic\'>Reminders cannot be set because the due date has passed</span>'
  }
  else if (taskForm.value.status == 'Completed') {
    return '<span class=\'text-xs italic\'>Reminders cannot be set because the task is completed</span>'
  }
  else {
    return null
  }
})

const {
  communicationStatusPicklist,
  conversionStatusPicklist,
  priorityPicklist,
  statusPicklist,
  typePicklist
} = useTasksPicklist()

const taskForm = ref({
  assignee: null,
  comment: '',
  communicationStatus: null,
  contact: {
    label: null,
    value: null
  },
  conversionStatus: null,
  date: new Date(),
  duration: 0,
  estimatedStartDate: null,
  priority: 'Normal',
  products: [],
  relatedTo: {
    icon: 'heroicons:building-office',
    type: 'Account',
    color: 'text-[#7F8DE1]',
    name: null,
    id: null,
    accountSearchTerm: null,
    accounts: []
  },
  status: 'Not Started',
  subject: null,
  type: null,
  customReminderDate: null,
  reminderInterval: null,
  userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
})

const existingAccountSelected = ref(false)

// Logic in worker file requires interval units to be in full days
const times = ref([
  { label: 'Custom', value: 'Custom'},
  { label: '1 day before', value: '1 day before'},
  { label: '2 days before', value: '2 days before'},
  { label: '3 days before', value: '3 days before'},
  { label: '4 days before', value: '4 days before'},
  { label: '5 days before', value: '5 days before'}
])

const availableTimes = computed(() => {
  const dueDate = new Date(taskForm.value.date)
  const now = new Date()

  // If the due date is today, only allow "Custom" option
  if (dueDate.toDateString() === now.toDateString()) {
    return [{ label: 'Custom', value: 'Custom'}]
  }

  // Otherwise, return all available times
  return times.value
})

const products = ref([
  {name: 'AHV'},
  {name: 'NAI'},
  {name: 'NCI'},
  {name: 'NCI Data'},
  {name: 'NCM'},
  {name: 'NDB'},
  {name: 'NKP'},
  {name: 'NUS'},
  {name: 'NC2-AWS'},
  {name: 'NC2-Azure'},
  {name: 'NC2 - GCP'},
  {name: 'DRaaS'},
  {name: 'EUC'},
])

const apiError = ref({
  show: false,
  tippy: null
})
const pnpRecordType = ref(false)
const deleteForm = ref(false)
const assignToArray = ref([])
const submitForm = ref(false)
const reminderKey = ref(0)

async function searchAssigneeByKeyword(options, query) {
  let results = (await v2.get(`/users/lookup?name=${query}`)).data
    .map(users => ({
      name: users.name,
      id: users.id,
      email: users.email,
    }))

  return uniqBy([...options, ...results], 'id')
}

/** ================================
 *        Related To
===================================*/
const editing = ref(false)
const relatedToOpen = ref(false)
const disableRelatedTo = ref(false)
const relatedToObjectTypeArray = ref([
  { type: 'Account', icon: 'heroicons:building-office', color: 'text-[#7F8DE1]' },
  { type: 'Opportunity', icon: 'heroicons:sparkles', color: 'text-[#FCB95B]' },
  { type: 'Product Adoption', icon: 'heroicons:chart-pie', color: 'text-[#618fd8]' }
])
const relatedToResults = ref([])
// Only provided by Task/Container.vue and Opportunity/Tasks.vue. ModalTask also
// renders inline from the globally-mounted AssistantChat.vue (/task, "Create
// follow-up actions"), which sits outside either of those provider trees — fall
// back to an empty ref there instead of crashing setup() on injecting undefined.
const myAccounts = inject('myAccounts', ref([]))
const myOpportunities = inject('myOpportunities', ref([]))
const myProductAdoptions = inject('myProductAdoptions', ref([]))

function selectRelatedToObjectType(task) {
  // Resetting Search Term on change of type if length
  relatedToResults.value = []
  // Resetting SelectedAccounts array
  selectedAccounts.value = []

  taskForm.value.relatedTo = {
    icon: task.icon,
    type: task.type,
    color: task.color,
    name: null,
    id: null,
    accountSearchTerm: null,
    accounts: []
  }

  relatedToOpen.value = false
}

const filteredResults = computed(() => {
  let results = []

  if (taskForm.value.relatedTo.type === 'Account') {
    results = myAccounts.value
  }
  else if (taskForm.value.relatedTo.type === 'Opportunity') {
    results = myOpportunities.value
  }
  else {
    results = myProductAdoptions.value
  }

  // Only show first 50 results
  results = results.slice(0, props.limit)

  results = results.map(r => {
    return {
      value: r.id,
      label: r.name,
      trailingIcon: {
        icon: 'heroicons:check-badge',
        color: 'text-green-700 dark:text-green-600',
        tippy: `My ${taskForm.value.relatedTo.type}`
      }
    }
  }) || []

  return uniqBy([...results, ...relatedToResults.value], 'value')
})

const urlMapping = ref({
  'Account': '/accounts?filter[search]',
  'Opportunity': '/opportunities?filter[search]',
  'Product Adoption': '/product-adoptions?filter[search]'
})

// Shows check mark for MY results that are outside of limited number shown on load
function searchResultIncludedInMyResults(result) {
  let resultInAccounts

  if (taskForm.value.relatedTo.type === 'Account') {
    resultInAccounts = myAccounts.value.filter(account => account.id === result.id).length > 0
  }
  else if (taskForm.value.relatedTo.type === 'Opportunity') {
    resultInAccounts = myOpportunities.value.filter(opportunity => opportunity.id === result.id).length > 0
  }
  else {
    resultInAccounts = myProductAdoptions.value.filter(pa => pa.id === result.id).length > 0
  }

  return resultInAccounts
}

async function searchForRelatedTo(options, query) {
  let results = (await v2.get(`${urlMapping.value[taskForm.value.relatedTo.type]}=${query}`)).data
  results = results.map(result =>  {
    if (taskForm.value.relatedTo.type === 'Product Adoption') {
      return {
        value: result.id,
        label: `${result.accountName} - ${result.product}`
      }
    }
    else {
      /**
       * Adding the checkmark to the search results that match ones found in the "My results" since we
       * are now limiting those to 50 results on instantiation
      */
      if (searchResultIncludedInMyResults(result)) {
        return {
          value: result.id,
          label: result.name,
          trailingIcon: {
            icon: 'heroicons:check-badge',
            color: 'text-green-700 dark:text-green-600',
            tippy: `My ${taskForm.value.relatedTo.type}`
          }
        }
      }

      return {
        value: result.id,
        label: result.name
      }
    }
  })

  return uniqBy([...options, ...results], 'value')
}

/**
 * Updated code for Account Relate To fields to allow multiple
 * Accounts to potentially be selected in the Related To section
*/
const accountsLoading = ref(false)
const relatedToForAccountsOpen = ref(false)
const myAccountsResults = ref(myAccounts.value)
const selectedAccounts = ref([])

const filteredAccounts = computed({
  get() {
    let results = myAccountsResults.value

    // Only show first 50 results
    results = results.slice(0, props.limit)

    /**
     * Adding the checkmark to the search results that match ones found in the "My results" since we
     * are now limiting those to 50 results on instantiation
    */
    results = results.map(result => {
      if (searchResultIncludedInMyResults(result)) {
        return {
          value: result.id,
          label: result.name,
          trailingIcon: {
            icon: 'heroicons:check-badge',
            tippy: 'My Account'
          }
        }
      }
      else {
        return {
          label: result.name,
          value: result.id
        }
      }

    }) || []

    return uniqBy([...results, ...relatedToResults.value], 'value')
  },

  set(newValue) {
    myAccountsResults.value = newValue
  }
})

const searchForRelatedAccounts = debounce(async () => {
  let searchTerm = taskForm.value.relatedTo.accountSearchTerm

  try {
    accountsLoading.value = true

    if (searchTerm && searchTerm.length >= 2) {
      filteredAccounts.value = (await v2.get(`${urlMapping.value[taskForm.value.relatedTo.type]}=${searchTerm}`)).data
    }
    else {
      filteredAccounts.value = myAccounts.value
    }
  }
  catch (error) {
    console.warn('Error searching accounts', error)
  }
  finally {
    accountsLoading.value = false
  }
}, 500)

function closeAccountSearchDropdown() {
  relatedToForAccountsOpen.value = false
  taskForm.value.relatedTo.accountSearchTerm = null
}

function selectRelatedToAccount(account) {
  let matchedAccount = taskForm.value.relatedTo.accounts.find(item => item === account.value)

  // To only allow the Account to be selected only once, if Account is already in array continue without adding
  if (matchedAccount) {
    return
  }
  else {
    // Setting account id into the accounts array
    taskForm.value.relatedTo.accounts.push(account.value)

    // Adding entire account object to the selectedAccounts array to display
    selectedAccounts.value.push(account)
  }
}

function clearSelectedAccount(accountName) {
  let matchedAccount = selectedAccounts.value.find(account => account.label === accountName)

  // Removing from selectedAccounts array
  selectedAccounts.value = selectedAccounts.value.filter(account => account.label !== matchedAccount.label)

  // Removing from Taskform related to accounts
  taskForm.value.relatedTo.accounts = taskForm.value.relatedTo.accounts.filter(accountId => accountId !== matchedAccount.value)
}

watch(() => taskForm.value.relatedTo.accountSearchTerm, (accountSearchTerm) => {
  if ((!relatedToForAccountsOpen.value && !accountSearchTerm)) {
    filteredAccounts.value = myAccounts.value
  }
})

/** ==============================
 * Product Functions
================================ */
function selectProduct(product) {
  if (taskForm.value.products.includes(product)) {
    taskForm.value.products = taskForm.value.products.filter(p => p !== product)
  }
  else {
    taskForm.value.products.push(product)
  }
}

/** ==============================
 *    Contact Functions
================================ */
const contactSearchResults = ref([])

async function searchContacts(options, query) {
  let results = (await v2.get(`/contacts?filter[search]=${query}`)).data
    .map(contact => ({
      label: contact.name,
      value: contact.id,
      email: contact.email
    }))

  return uniqBy([...options, ...results], 'value')
}

/** ==============================
 *    Miscellaneous Functions
================================ */
function submitTask() {
  submitForm.value = true

  const taskToSubmit = {
    id: editing.value ? props.taskToUpdate.id : '',
    type: taskForm.value.type,
    dueDate: taskForm.value.date,
    estimatedStartDate: taskForm.value.estimatedStartDate,
    details: {
      priority: taskForm.value.priority,
      status: taskForm.value.status,
      contactId: taskForm.value.contact.label?.value,
      comment: taskForm.value.comment,
      relatedTo: taskForm.value.relatedTo.type === 'Account' ? taskForm.value.relatedTo.accounts : [taskForm.value.relatedTo.id?.value],
      duration: taskForm.value.duration !== 0 ? taskForm.value.duration : null,
      communicationStatus: taskForm.value.communicationStatus,
      conversionStatus: taskForm.value.conversionStatus,
    },
    title: taskForm.value.subject,
    assignee: taskForm.value.assignee?.id || null,
    products: taskForm.value.products.length ? taskForm.value.products.join(';') : null,
    reminderInterval: remindMe.value ? taskForm.value.reminderInterval : null,
    customReminderDate: remindMe.value && taskForm.value.reminderInterval === 'Custom' ? taskForm.value.customReminderDate : null,
    userTimezone: taskForm.value.userTimezone
  }

  emit('submitTask', taskToSubmit, (response) => {
    if (response) {
      submitForm.value = false
      apiError.value.show = true
      apiError.value.tippy = response
    }
  })
}

function setRelatedToIconAndColor(task) {
  switch (task) {
  case 'Account':
    taskForm.value.relatedTo.icon = 'heroicons:building-office'
    taskForm.value.relatedTo.color = 'text-[#7F8DE1]'
    break

  case 'Opportunity':
    taskForm.value.relatedTo.icon = 'heroicons:sparkles'
    taskForm.value.relatedTo.color = 'text-[#FCB95B]'
    break

  case 'Product_Adoption__c':
    taskForm.value.relatedTo.icon = 'heroicons:chart-pie'
    taskForm.value.relatedTo.color = 'text-[#618fd8]'
    break

  default:
    taskForm.value.relatedTo.icon = 'heroicons:question-mark-circle'
    taskForm.value.relatedTo.color = 'text-charcoal-500'
    break
  }
}

/**
 * After setting a dueDate in the future, this function allows you to set a customer
 * reminder all the way up until the end of the day on the dueDate.
*/
function determineReminderConstrict(date) {
  const constricted = setMinutes(setHours(date, 23), 59)
  return constricted
}

function isNullish(obj) {
  return Object.values(obj).every(value => {
    if ( value === null ) {
      return true
    }
    return false
  })
}

const durationArray = computed(() => {
  const range = (start, end, step) => {
    return Array.from(Array.from(Array((end-start)/step).keys()), num => start + num * step)
  }
  return range(.25,24.25,.25).map(time => ({ label: time, value: time }))
})

const formSubmitDisabled = computed(() => {
  // Ensure that if the user picks 'Custom' for their reminder they must choose a date to submit the task
  const isCustomIntervalSelected = taskForm.value.reminderInterval === 'Custom'

  if (!submitForm.value &&
      taskForm.value.date && taskForm.value.assignee &&
      taskForm.value.subject &&
      taskForm.value.status &&
      taskForm.value.priority &&
      ((remindMe.value && taskForm.value.reminderInterval && !isCustomIntervalSelected) || (remindMe.value && isCustomIntervalSelected && taskForm.value.customReminderDate) || !remindMe.value)
  ) {
    return false
  }

  return true
})

const priorityIconColor = computed(() => {
  if (taskForm.value.priority === 'High') {
    return 'text-peach-900 dark:text-peach-800'
  }
  else if(taskForm.value.priority === 'Low') {
    return 'text-aqua-900 dark:text-aqua-600'
  }
  else {
    return 'text-charcoal-800 dark:text-charcoal-300'
  }
})

// Watchers to help the task reminder fields
watch([() => taskForm.value.status, () => taskForm.value.date], ([newStatus]) => {
  // If the status is 'Completed' or if the due date has passed, clear the reminder fields
  if (newStatus === 'Completed' || dueDateHasPassed.value) {
    remindMe.value = false
    taskForm.value.reminderInterval = null
    taskForm.value.customReminderDate = null
  }
})

watch(() => taskForm.value.date, (newDate, oldDate) => {
  // If the due date changes to today and thus excludes certain reminderInterval choices, reset the reminderInterval field
  if (newDate !== oldDate && !availableTimes.value.includes(taskForm.value.reminderInterval)) {
    taskForm.value.reminderInterval = null
  }
})

watch(() => taskForm.value.customReminderDate, (newDate) => {
  // Force the custom reminder datepicker always rounds to nearest 30min
  if (newDate instanceof Date && newDate.getMinutes() % 30 !== 0) {
    const roundedDate = roundToNearestMinutes(newDate, { nearestTo: 30, roundingMethod: 'ceil' })

    // Update if the rounded date is different from the current date
    if (roundedDate.getTime() !== newDate.getTime()) {
      taskForm.value.customReminderDate = roundedDate
    }
  }
})

watch(availableTimes, () => {
  // Force Listbox re-render when availableTimes changes
  reminderKey.value += 1
})

onMounted(async () => {
  // New Task - From Top Level Tasks (Account/Opportunity/Product Adoption) and Not Updating
  if (!isEmpty(props.fromTopLevelObject) && isEmpty(props.taskToUpdate)) {

    // Disable Related To field
    disableRelatedTo.value = true

    // Setting type of Record Type coming from Top Level
    if (props.fromTopLevelObject.type === 'Product_Adoption__c') {
      taskForm.value.relatedTo.type = 'Product Adoption'
    } else {
      taskForm.value.relatedTo.type = props.fromTopLevelObject.type
    }

    // If Related To equals Account, set existing account boolean to true
    if (taskForm.value.relatedTo.type === 'Account') {
      existingAccountSelected.value = true
    }

    taskForm.value.relatedTo.name = props.fromTopLevelObject.name
    taskForm.value.relatedTo.id = props.fromTopLevelObject.id
    setRelatedToIconAndColor(props.fromTopLevelObject.type)
    // Setting relatedTo for Top Level Task
    relatedToResults.value.push({ label:taskForm.value.relatedTo.name , value: taskForm.value.relatedTo.id })

    // Setting assignee
    taskForm.value.assignee = props.assignedUser
    assignToArray.value.push({
      name: props.assignedUser.name,
      value: props.assignedUser.id
    })
  }

  // If updating a tasks
  else if (!isEmpty(props.taskToUpdate)) {
    editing.value = true

    // Setting taskForm to the values from taskToUpdate
    taskForm.value.comment =  props.taskToUpdate.data.comment
    taskForm.value.communicationStatus = props.taskToUpdate.data.communicationStatus
    taskForm.value.contact.label = props.taskToUpdate.data.contact.name
    taskForm.value.contact.value = props.taskToUpdate.data.contact.id
    taskForm.value.conversionStatus = props.taskToUpdate.data.conversionStatus
    taskForm.value.date = props.taskToUpdate.data.date ? prettyDateToDateString(props.taskToUpdate.data.date) : null
    taskForm.value.duration = props.taskToUpdate.data.duration || null
    taskForm.value.estimatedStartDate = props.taskToUpdate.data.estimatedStartDate ? prettyDateToDateString(props.taskToUpdate.data.estimatedStartDate) : null
    taskForm.value.priority = props.taskToUpdate.data.priority
    taskForm.value.products = props.taskToUpdate.data.products ? props.taskToUpdate.data.products.split(';') : []
    taskForm.value.status = props.taskToUpdate.data.status
    taskForm.value.subject = props.taskToUpdate.data.subject
    taskForm.value.type = props.taskToUpdate.data.type || '' //To handle SFDC not liking our null value we receive from them

    // Setting boolean for when editng PnP 2.0 Record Type tasks
    if(props.taskToUpdate.recordType.name === 'PnP 2.0 Conversion') {
      pnpRecordType.value = true
      disableRelatedTo.value = true
    }

    // SETTING UP TASK CONTACT IF TASK HAS ONE
    if (props.taskToUpdate.data.contact.id) {
      taskForm.value.contact.label = props.taskToUpdate.data.contact.name
      taskForm.value.contact.value = props.taskToUpdate.data.contact.id
      contactSearchResults.value.push({
        label: props.taskToUpdate.data.contact.name,
        value: props.taskToUpdate.data.contact.id
      })
    }

    // If the task has an associated due reminder, we set the taskform values & turn on the toggle
    if (props.taskToUpdate.data.dueReminder.reminderInterval && !dueDateHasPassed.value && props.taskToUpdate.data.status != 'Completed') {
      taskForm.value.reminderInterval = props.taskToUpdate.data.dueReminder.reminderInterval
      taskForm.value.customReminderDate = props.taskToUpdate.data.dueReminder.customReminderDate
      remindMe.value = true
    }

    // SETTING UPDATE TASK ASSIGNEE OF CURRENT TASK
    if (props.taskToUpdate.assignee.id) {
      taskForm.value.assignee = props.taskToUpdate.assignee
      assignToArray.value.push({
        name: props.taskToUpdate.assignee.name,
        value: props.taskToUpdate.assignee.id
      })
    }

    // SETTING UPDATE TASK RELATED TO
    if (!isNullish(props.taskToUpdate.data.relatedTo)) {
      disableRelatedTo.value = true
      // Setting relatedTo placeholder type
      if (props.taskToUpdate.data.relatedTo.type === 'Product_Adoption__c') {
        taskForm.value.relatedTo.type = 'Product_Adoption__c'
      } else {
        taskForm.value.relatedTo.type = props.taskToUpdate.data.relatedTo.type
      }

      // If Related To equals Account, set existing account boolean to true and make related to disabled
      if (taskForm.value.relatedTo.type === 'Account') {
        existingAccountSelected.value = true
      }

      // Handles populating the Account InputText
      taskForm.value.relatedTo.name = props.taskToUpdate.data.relatedTo.name
      // Handles populating the Opp/PA Autocomplete
      taskForm.value.relatedTo.id = {
        label: props.taskToUpdate.data.relatedTo.name,
        value: props.taskToUpdate.data.relatedTo.id
      }
      setRelatedToIconAndColor(taskForm.value.relatedTo.type)
      // Setting relatedTo
      relatedToResults.value.push({ label:taskForm.value.relatedTo.name , value: taskForm.value.relatedTo.id })
    }
  }
  else {
    // From Team View
    if (props.team) {
      // Adding team members to assignToArray
      assignToArray.value = props.team.team.map(teamMember => ({
        name: teamMember.name,
        value: teamMember.id
      }))
      // Setting team assignee
      if (props.team.id) {
        taskForm.value.assignee = props.team
      }
    }
    // New Task - not Top Level Object
    else {
      // Setting assignee
      taskForm.value.assignee = props.assignedUser
      assignToArray.value.push({
        name: props.assignedUser.name,
        value: props.assignedUser.id
      })
    }
  }

  // For Type Account setting id into an array
  if (taskForm.value.relatedTo.type === 'Account') {
    if (taskForm.value.relatedTo.id === null) {
      taskForm.value.relatedTo.accounts = []
    }
    else {
      taskForm.value.relatedTo.accounts = [taskForm.value.relatedTo.id.value ?? taskForm.value.relatedTo.id]
    }
  }
})
</script>
